import CustomCard from "components/common/CustomCard";
import CardHeader from "components/common/CardHeader";
import Row from "components/common/Row";
import CardBody from "components/common/CardBody";
import Column from "components/common/Column";
import AutoCompleteDropdown from "components/autocomplete/AutoCompleteDropdown";
import ActionButton from "components/common/ActionButton";
import React, { useMemo, useState } from "react";
import { getFormattedDate, getStatus } from "helpers";
import Loader from "components/spinner/Loader";

const TaskTabsFilters = ({ config, missionManger, movements, movementData, reloadData, showHeading }) => {

    const [filters, setFilters] = useState({
        country: null,
        activity_type: null,
        mission_phase: null,
        due_date: null,
        status: null,
        person_assigned: null,
    });
    const [loader, setLoader] = useState({
        filtering: false,
        reseting: false,
    });

    const handleFilter = () => {
        setLoader({ ...filters, filtering: true });
        const filterData = movements.filter(d => {
            if (filters.country && d.mission?.country?.id !== filters.country) return false;
            if (filters.activity_type && d.mission?.activity_type?.id !== filters.activity_type) return false;
            if (filters.mission_phase && d?.relationForm?.missionPhase?.id !== filters.mission_phase) return false;
            if (filters.due_date && d.due_date !== filters.due_date) return false;
            if (filters.person_assigned && d.user_id !== parseInt(filters.person_assigned)) return false;
            if (filters.status) {
                const [status, is_enabled] = filters.status.split(', ');
                if (parseInt(status) === parseInt(d.status) && d.is_enabled === (is_enabled === 'true')) return true;
                return false;
            }
            return true;
        });
        reloadData(filterData);
        setLoader({ ...filters, filtering: false });
    }

    const handleResetFilters = () => {
        setLoader({ ...filters, reseting: true });
        setFilters({
            country: null,
            activity_type: null,
            mission_phase: null,
            due_date: null,
            status: null,
            person_assigned: null,
        });
        reloadData(movements);
        setLoader({ ...filters, reseting: false });
    }

    const countriesOption = useMemo(() => {
        let countries = [];
        movementData?.forEach(movement => {
            if (!countries.some(country => country.value === movement?.mission?.country?.id)) {
                countries.push({
                    value: movement?.mission?.country?.id,
                    label: movement?.mission?.country?.country_name
                });
            }
        });
        return countries;
    }, [movementData]);
    const missionTypesOption = useMemo(() => {
        let missionTypes = [];
        movementData?.forEach(movement => {
            if (!missionTypes.some(type => type.value === movement?.mission?.activity_type?.id)) {
                missionTypes.push({
                    value: movement?.mission?.activity_type?.id,
                    label: movement?.mission?.activity_type?.name
                });
            }
        });
        return missionTypes;
    }, [movementData]);
    const missionPhaseOption = useMemo(() => {
        let missionPhases = [];
        movementData?.forEach(movement => {
            if (!missionPhases.some(phase => phase.value === movement?.relationForm?.missionPhase?.id)) {
                missionPhases.push({
                    value: movement?.relationForm?.missionPhase?.id,
                    label: movement?.relationForm?.missionPhase?.list_element_name
                });
            }
        });
        return missionPhases;
    }, [movementData]);


    const dueDateOption = useMemo(() => {
        const dueDates = [];
        movementData?.forEach(movement => {
            const dueDate = movement?.due_date;
            const dueDateFormatted = getFormattedDate(movement?.due_date_formatted);
            // Skip invalid or duplicate dates
            if (
                ![undefined, null, ''].includes(dueDate) &&
                !dueDates.some(date => date.value === dueDate)
            ) {
                dueDates.push({
                    value: dueDate,
                    label: dueDateFormatted
                });
            }
        });
        return dueDates;
    }, [movementData]);

    const statusOption = useMemo(() => {
        let statuses = [];
        movementData?.forEach(movement => {
            const value = movement?.status + ', ' + movement.is_enabled;
            if (!statuses.some(status => status.value === value)) {
                const label = getStatus(
                    movement?.status,
                    movement?.user_id,
                    missionManger,
                    movement?.dform_assigned_for,
                    movement?.is_enabled,
                    movement?.relationForm?.relationType?.ref_id,
                );
                statuses.push({
                    value: value,
                    label: label
                });
            }
        });
        return statuses;
    }, [movementData]);
    const personAssignedOption = useMemo(() => {
        let users = [];
        movementData?.forEach(movement => {
            if (!users.some(user => user.value === movement?.user_id)) {
                users.push({
                    value: movement?.user_id,
                    label: movement?.assigned_to
                });
            }
        });
        return users;
    }, [movementData]);

    return (
        <React.Fragment>
            <CustomCard className='mb-2 border-bottom-0 shadow-none'>
                {showHeading && <CardHeader>
                    <h3>Filters</h3>
                </CardHeader>}
                <CardBody>
                    <Row>
                        {
                            config.showCountry && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Countries"}
                                        multiple={false}
                                        hasselectall={true}
                                        options={countriesOption}
                                        onChange={(event, value) => setFilters({
                                            ...filters, country: value?.value
                                        })}
                                        value={countriesOption.find(val => parseInt(val.value) === parseInt(filters.country)) ?? null}
                                    />
                                </Column>
                            )
                        }
                        {
                            config.showMissionType && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Mission Type"}
                                        multiple={false}
                                        hasselectall={true}
                                        options={missionTypesOption}
                                        onChange={(event, value) => setFilters({
                                            ...filters, activity_type: value?.value
                                        })}
                                        value={missionTypesOption.find(val => parseInt(val.value) === parseInt(filters.activity_type)) ?? null}
                                    />
                                </Column>
                            )
                        }
                        {
                            config.showMissionPhase && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Mission Phase"}
                                        overflowY="hidden"
                                        multiple={false}
                                        hasselectall={true}
                                        options={missionPhaseOption}
                                        onChange={(event, value) => setFilters({
                                            ...filters, mission_phase: value?.value
                                        })}
                                        value={missionPhaseOption.find(val => parseInt(val.value) === parseInt(filters.mission_phase)) ?? null}
                                    />
                                </Column>
                            )
                        }
                        {
                            config.showDueDate && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Due Date"}
                                        overflowY="hidden"
                                        multiple={false}
                                        hasselectall={true}
                                        options={dueDateOption}
                                        onChange={(event, value) => setFilters({
                                            ...filters, due_date: value?.value
                                        })}
                                        value={dueDateOption.find(val => parseInt(val.value) === parseInt(filters.due_date)) ?? null}
                                    />
                                </Column>
                            )
                        }
                        {
                            config.showStatus && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Processes"}
                                        overflowY="hidden"
                                        multiple={false}
                                        hasselectall={true}
                                        options={statusOption}
                                        onChange={(event, value) => setFilters({
                                            ...filters, status: value?.value
                                        })}
                                        value={statusOption.find(val => (val.value) === (filters.status)) ?? null}
                                    />
                                </Column>
                            )
                        }
                        {
                            config.showPersonAssigned && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Person Assigned"}
                                        overflowY="hidden"
                                        multiple={false}
                                        hasselectall={true}
                                        options={personAssignedOption}
                                        onChange={(event, value) => setFilters({
                                            ...filters, person_assigned: value?.value
                                        })}
                                        value={personAssignedOption.find(val => parseInt(val.value) === parseInt(filters.person_assigned)) ?? null}
                                    />
                                </Column>
                            )
                        }
                        {
                            config.showYear && (
                                <Column col={2}>
                                    <AutoCompleteDropdown
                                        required={true}
                                        error={true}
                                        label={"Year"}
                                        overflowY="hidden"
                                        multiple={false}
                                        hasselectall={true}
                                    />
                                </Column>
                            )
                        }
                        <Column col={2} className='d-flex justify-content-end align-items-center'>
                            <ActionButton
                                variant='orange'
                                className='px-5 py-3'
                                sx={{
                                    fontWeight: '500',
                                    fontSize: '1.2rem',
                                }}
                                onClick={handleFilter}
                            >
                                {
                                    loader.filtering ? <Loader message='Filtering...' /> : "Filter"
                                }
                            </ActionButton>
                            <ActionButton
                                variant='dark'
                                className='ms-1 px-5 py-3'
                                sx={{
                                    fontWeight: '500',
                                    fontSize: '1.2rem',
                                }}
                                onClick={handleResetFilters}
                            >
                                {
                                    loader.reseting ? <Loader message='Reseting...' /> : "Reset Filter"
                                }
                            </ActionButton>
                        </Column>
                    </Row>
                </CardBody>
            </CustomCard>
        </React.Fragment>
    );
}

TaskTabsFilters.defaultProps = {
    config: {
        showCountry: true,
        showMissionType: true,
        showMissionPhase: false,
        showDueDate: true,
        showStatus: true,
        showPersonAssigned: true,
        showYear: false,
    },
    movements: [],
    movementData: [],
    reloadData: () => { },
};

export default TaskTabsFilters;